import React, { memo, useEffect, useMemo, useState, type FC } from 'react';
import { AccessibilityInfo, Platform, Text as RNText, TextStyle } from 'react-native';

import { useScaling } from '@/hooks';
import { theme } from '@/theme/theme';
import { fontFamilies, resolveFontFamily } from '@/theme/typography';
import { TextProps } from './types';
import { getIosBoldTextAdjustedFontWeightStyle } from './ios-bold-text-font-weight';

const Text: FC<TextProps> = ({
  children,
  variant,
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing,
  style,
  transform,
  fontStyle = 'normal',
  ...rest
}) => {
  const { scale } = useScaling();
  const [iosBoldTextEnabled, setIosBoldTextEnabled] = useState(false);

  const baseStyle = useMemo(() => {
    if (!variant) return undefined;
    const t = theme.typography[variant];
    // fontWeight is intentionally omitted here — `typographyOverride` below applies it (or
    // deliberately drops it for exact-face fonts like Open Sans, where a separate fontWeight
    // would make iOS ignore the resolved face and collapse every weight to one thickness).
    return {
      fontFamily: t.fontFamily,
      fontSize: scale(t.fontSize),
      lineHeight: scale(t.lineHeight),
      letterSpacing: t.letterSpacing,
      textTransform: t.textTransform,
    } as TextStyle;
  }, [variant, scale]);

  const typographyOverride = useMemo(() => {
    const overrides: {
      fontFamily?: string;
      fontSize?: TextProps['fontSize'];
      fontWeight?: TextProps['fontWeight'];
      lineHeight?: TextProps['lineHeight'];
      letterSpacing?: TextProps['letterSpacing'];
      fontStyle?: TextProps['fontStyle'];
    } = {};

    const _fontFamily = (() => {
      if (fontFamily) return fontFamily;
      if (variant) return theme.typography[variant].fontFamily;
      return fontFamilies.inter;
    })();
    const _fontWeight = (() => {
      if (fontWeight) return fontWeight;
      if (variant) return theme.typography[variant].fontWeight;
      return '400';
    })();
    const _fontStyle = (() => {
      if (fontStyle) return fontStyle;
      return 'normal';
    })();
    const resolvedFontFamily = resolveFontFamily({
      fontFamily: _fontFamily,
      fontWeight: _fontWeight,
      fontStyle: _fontStyle,
    });
    // When the family resolves to a specific weighted face (e.g. 'OpenSans-SemiBold'), the weight
    // is already baked into the face name. Passing fontWeight on top makes iOS re-derive the
    // family and collapse all weights to one thickness, so only apply it when the family itself
    // carries the weight (Inter/Montserrat on iOS, where the resolved name equals the family).
    const isExactFace = resolvedFontFamily.fontFamily !== _fontFamily;

    if (resolvedFontFamily.fontFamily) overrides.fontFamily = resolvedFontFamily.fontFamily;
    if (fontSize) overrides.fontSize = fontSize;
    if (_fontWeight && Platform.OS !== 'android' && Platform.OS !== 'web' && !isExactFace) {
      overrides.fontWeight = _fontWeight;
    }
    if (lineHeight) overrides.lineHeight = lineHeight;
    if (letterSpacing) overrides.letterSpacing = letterSpacing;
    if (fontStyle) overrides.fontStyle = fontStyle;
    return Object.keys(overrides).length > 0 ? overrides : undefined;
  }, [fontFamily, fontSize, fontWeight, lineHeight, letterSpacing, variant, fontStyle]);

  const textTransform = useMemo(() => {
    return transform ? { textTransform: transform } : undefined;
  }, [transform]);

  const iosBoldTextTypographyStyle = useMemo(
    () =>
      iosBoldTextEnabled
        ? getIosBoldTextAdjustedFontWeightStyle(typographyOverride?.fontWeight)
        : undefined,
    [iosBoldTextEnabled, typographyOverride],
  );

  useEffect(() => {
    if (Platform.OS !== 'ios') {
      setIosBoldTextEnabled(false);
      return;
    }

    const sync = (enabled: boolean) => {
      setIosBoldTextEnabled(enabled);
    };

    let cancelled = false;
    (async () => {
      const enabled = await AccessibilityInfo.isBoldTextEnabled();
      if (!cancelled) sync(enabled);
    })();

    const subscription = AccessibilityInfo.addEventListener('boldTextChanged', sync);
    return () => {
      cancelled = true;
      subscription.remove();
    };
  }, []);

  return (
    <RNText
      allowFontScaling={false}
      {...rest}
      style={[baseStyle, typographyOverride, iosBoldTextTypographyStyle, textTransform, style]}>
      {children}
    </RNText>
  );
};

export default memo(Text);
